(function () {
    'use strict';

    angular
        .module('app')
        .directive('drvItemList', drvItemList);

    /**
     * @ngdoc directive
     * @name drvItemList
     *
     * @description
     * Show a list of items.
     *
     * @param {number} parentItemId - The id of the 'parent' or 'owner' item to filter the list against.
     * @param {object} api - Optional. If provided, will have functions added to it for interacting with the item list:
     *   - {function} refresh - Refresh the item list.
     * @param {bool} showRelatedItemColumn
     * @param {function} listUpdated - Optional. Callback, triggered when list is updated.
     */
    function drvItemList() {
        return {
            templateUrl: 'app/item/item-list.tmpl.html',
            scope: {},
            controller: drvItemListCtrl,
            controllerAs: 'drvCtrl',
            bindToController: {
                parentItemId: '<',
                api: '=',
                showRelatedItemColumn: '<',
                listUpdated: '&?',
                excludeTasks: '<?'
            }
        };
    }

    drvItemListCtrl.$inject = [
        '$q',
        '$scope',
        '$timeout',
        '$location',
        'ItemService',
        'TagsService',
        'NotesService',
        'DialogService',
        'SystemService',
        'EntitiesService',
        'DmsService',
        'Task',
        'PromiseLatest'];

    function drvItemListCtrl(
        $q,
        $scope,
        $timeout,
        $location,
        ItemService,
        TagsService,
        NotesService,
        DialogService,
        SystemService,
        EntitiesService,
        DmsService,
        Task,
        PromiseLatest) {

        /* jshint validthis:true */
        var drvCtrl = this;

        drvCtrl.items = [];
        drvCtrl.itemCount = 0;
        drvCtrl.currentPage = 1;
        drvCtrl.pageSize = 10;
        drvCtrl.loading = false;
        drvCtrl.error = '';
        drvCtrl.users = [];
        drvCtrl.tags = [];
        drvCtrl.updateList = updateList;
        drvCtrl.searchDescription = '';
        drvCtrl.searchRecordType = '';
        drvCtrl.searchUser = null;
        drvCtrl.searchTagIds = [];
        drvCtrl.userEntityTemplateName = 'User';
        drvCtrl.getRecordTypes = getRecordTypes;
        drvCtrl.getDefaultSearchRecordTypes = getDefaultSearchRecordTypes;
        drvCtrl.downloadFile = downloadFile;
        drvCtrl.onExport = onExport;
        drvCtrl.onImport = onImport;
        drvCtrl.onDelete = onDelete;
        drvCtrl.deleteItem = deleteItem;
        drvCtrl.onCopy = onCopy;
        drvCtrl.onBulkEdit = onBulkEdit;

        var updateListPromise = new PromiseLatest();

        activate();

        function activate() {
            if (!_.isNil(drvCtrl.api)) {
                drvCtrl.api.refresh = updateList;
            }

            drvCtrl.searchDelay = SystemService.getSearchDelay();

            if (!_.isNil(drvCtrl.parentItemId)) {
                updateList();
            }

            updateTags();
            loadUsers();

            function searchVariableChanged(newVal, oldVal) {
                if (newVal != oldVal) {
                    updateList();
                }
            }

            $scope.$watch('drvCtrl.parentItemId', searchVariableChanged);
            $scope.$watch('drvCtrl.searchRecordType', searchVariableChanged);
            $scope.$watch('drvCtrl.searchDescription', searchVariableChanged);
            $scope.$watch('drvCtrl.searchUser', searchVariableChanged);
            $scope.$watch('drvCtrl.searchTagIds', searchVariableChanged);
            $scope.$watch('drvCtrl.showDeleted', searchVariableChanged);
        }

        function getRecordTypes() {
            var result = [
                { value: 'Email', description: 'Email' },
                { value: 'File', description: 'File' },
                { value: 'Note', description: 'Note' },
                { value: 'SMS', description: 'SMS' }];

            if (!drvCtrl.excludeTasks) {
                result.push({ value: 'Task', description: 'Task' });
            }

            if (_.isNil(getRecordTypes.result) || getRecordTypes.result.length !== result.length) {
                getRecordTypes.result = result;
            }

            return getRecordTypes.result;
        }

        function getDefaultSearchRecordTypes() {
            var result = _.map(drvCtrl.getRecordTypes(), function (recordType) { return recordType.value; });

            if (_.isNil(getDefaultSearchRecordTypes.result) || getDefaultSearchRecordTypes.result.length !== result.length) {
                getDefaultSearchRecordTypes.result = result;
            }

            return getDefaultSearchRecordTypes.result;
        }

        /**
         * Update the data for the item list.
         *
         * @param {number} delay - Optional. A delay, in milliseconds, for debounce-like functionality.
         *   data will not be updated until the delay elapses, unless a subsequent call with a shorter
         *   remaining delay is made.
         */
        function updateList(delay) {
            if (!_.isNil(updateList.timeout)) {
                $timeout.cancel(updateList.timeout);
                updateList.timeout = null;
            }

            if (_.isNil(delay) || delay === 0) {
                getItems();
            } else {
                $timeout(getItems, delay);
            }

            function getItems() {
                drvCtrl.loading = true;

                var searchRecordType = drvCtrl.getDefaultSearchRecordTypes();
                if (!_.isNil(drvCtrl.searchRecordType) && drvCtrl.searchRecordType !== '') {
                    searchRecordType = drvCtrl.searchRecordType;
                }

                updateListPromise.latestOnly(ItemService.getItemsByParent(drvCtrl.parentItemId, {
                    recordType: searchRecordType,
                    description: drvCtrl.searchDescription,
                    userId: drvCtrl.searchUser,
                    tagIds: drvCtrl.searchTagIds,
                    pageNumber: drvCtrl.currentPage,
                    pageSize: drvCtrl.pageSize,
                    excludeLinkTypes: ['Entity Image']
                }))
                    .then(getItemsOk, getItemsError)
                    .finally(function () { drvCtrl.loading = false; });
            }

            function getItemsOk(items) {
                _.forEach(items.Items, function (item) {
                    formatItemForView(item);
                });

                _.forEach(items.Items, function (item) {
                    _.forEach(item.Owners, function (itemOwner) {
                        formatItemForView(itemOwner, true);
                    });
                });

                drvCtrl.items = items.Items;
                drvCtrl.itemCount = items.ItemCount;
                mergeTagData();

                if (angular.isFunction(drvCtrl.listUpdated)) {
                    drvCtrl.listUpdated();
                }
            }

            function getItemsError(response) {
                drvCtrl.error = response;
            }

            /**
             * Format an "Item" for viewing. Some properties may be added, others may be modified.
             *
             * @param {object} item - The item to be formatted.
             * @param {bool} includeTypePrefix - True if item.Description should be prefixed with
             *   the item type / entity template name.
             */
            function formatItemForView(item, includeTypePrefix) {
                item.canEdit = false;
                item.iconUsage = item.Type;
                item.icon = '';

                if (item.Type === 'SMS') {
                    item.canEdit = true;
                    item.editLink = '#/sms/' + item.Id;
                    item.iconUsage = '';
                    item.icon = 'sms-sent';
                } else if (item.Type === 'Note') {
                    item.canEdit = true;
                    item.editLink = '#/notes/' + item.Id;
                } else if (item.Type === 'File') {
                    item.canEdit = true;
                    item.editLink = '#/files/' + item.Id;
                } else if (item.Type === 'Email') {
                    item.canEdit = true;
                    item.editLink = '#/email/' + item.Id;
                    if (item.EmailStatus === 'Sent') {
                        item.iconUsage = 'email-sent';
                    } else if (item.EmailStatus === 'Received') {
                        item.iconUsage = 'email-received';
                    } else if (item.EmailStatus === 'Draft') {
                        item.iconUsage = 'email-draft';
                    }
                } else if (item.Type === 'Entity') {
                    item.canEdit = true;
                    item.editLink = '#/entities/' + item.Id;
                }

                if (includeTypePrefix) {
                    if (item.EntityTemplateName) {
                        item.Description = item.EntityTemplateName + ' - ' + item.Description;
                    } else {
                        item.Description = item.Type + ' - ' + item.Description;
                    }
                }
            }

        }

        /**
         * Get all tags
         */
        function updateTags() {
            TagsService.query()
                .then(getTagsOk, getTagsError);

            function getTagsOk(data) {
                drvCtrl.tags = data.Tags;
                mergeTagData();
            }

            function getTagsError(response) {
                DialogService.error('Error loading item Tags', response);
            }
        }

        /**
         * Merge tag data with item data for presentation.
         */
        function mergeTagData() {

            // item.Tags is a list of tag ids (numbers)
            // tags is a list of Tag objects, where Id is the tag id

            if (!_.isNil(drvCtrl.items) && !_.isNil(drvCtrl.tags) && drvCtrl.tags.length > 0) {
                _.forEach(drvCtrl.items, function (item) {
                    item.PopulatedTags = _.filter(drvCtrl.tags, function (t) {
                        return _.includes(item.Tags, t.Id);
                    });
                });
            }
        }

        /**
         * Load all users.
         */
        function loadUsers() {
            return EntitiesService.query({ entityType: 'User' })
                .then(getUsersOk, getUsersError);

            function getUsersOk(data) {
                drvCtrl.users = data.Entities;
                
                if (data.Entities.length > 0) {
                    drvCtrl.userEntityTemplateName = data.Entities[0].EntityTemplate.Name;
                }
            }

            function getUsersError(response) {
                DialogService.error('Error fetching Users', response);
            }
        }

        /**
         * Download the file associated with the specified item id.
         *
         * @param {number} id - Item/File id.
         */
        function downloadFile(id) {
            DmsService.retrieveFile(id, true)
                .catch(downloadFileError);

            function downloadFileError(response) {
                DialogService.error('Error downloading File', response);
            }
        }

        function onExport() {
            var selectedNoteIds = _.chain(drvCtrl.items).filter({ Type: 'Note', Selected: true }).map('Id').value();
            NotesService.exportNotes(drvCtrl.parentItemId, selectedNoteIds);
        }

        function onImport() {
            NotesService.importNotes(drvCtrl.parentItemId)
                .then(importOk);

            function importOk() {
                updateList();
            }
        }

        /**
         * Get the Id's of the items selected in the view.
         *
         * @return {array} - Array of {number}
         */
        function getSelectedItemIds() {
            return _.chain(drvCtrl.items).filter('Selected').map('Id').value();
        }

        /**
         * Ensure at least one Item is selected.
         * Displays a dialog 'No Items were selected' if no Items are selected.
         *
         * @return {bool} true if at least one Item is selected, else false
         */
        function ensureItemSelection() {
            if (getSelectedItemIds().length === 0) {
                DialogService.alert('No Items were selected.');
                return false;
            } else {
                return true;
            }
        }

        /**
         * Delete the selected items.
         */
        function onDelete() {
            if (ensureItemSelection()) {
                deleteItems(getSelectedItemIds());
            }
        }

        /**
         * Delete the specified item.
         *
         * @param {number} itemId
         */
        function deleteItem(itemId) {
            deleteItems([itemId]);
        }

        /**
         * Delete the specified items.
         *
         * @param {array} itemIds - An array of {number}
         */
        function deleteItems(itemIds) {
            if (itemIds.length > 0) {
                var confirmMessage = 'Delete the selected Item?';
                if (itemIds.length > 1) {
                    confirmMessage = 'Delete the ' + itemIds.length + ' selected Items?';
                }

                DialogService.confirm(confirmMessage, 'Item delete confirm')
                    .then(deleteConfirmed);
            }

            function deleteConfirmed() {
                ItemService.deleteItems(itemIds)
                    .catch(deleteItemsError)
                    .finally(updateList);
            }

            function deleteItemsError(response) {
                DialogService.error('Error deleting items', response);
            }
        }

        /**
         * Copy the selected items.
         */
        function onCopy() {
            if (!ensureItemSelection()) {
                return;
            }

            // Convert list of selected items into "item copy state" list
            var itemCopyState = [];
            _.forEach(_.filter(drvCtrl.items, 'Selected'), function (item) {
                var itemMetaData = ItemService.getItemTypeMetaData(item);

                itemCopyState.push({
                    item: item,
                    status: itemMetaData && itemMetaData.canCopy ? ItemService.itemCopyStatuses.ToBeCopied : ItemService.itemCopyStatuses.ItemTypeCannotBeCopied
                });
            });


            showCopyConfirm()
                .then(copyItems);


            /**
             * Show a confirmation dialog detailing the items that will be copied.
             *
             * @return {promise} Promise resolves if the user confirms they want to copy the selected items.
             */
            function showCopyConfirm() {
                var itemsToCopy = _.chain(itemCopyState).filter({ status: ItemService.itemCopyStatuses.ToBeCopied }).map('item').value();
                var itemsNotCopied = _.chain(itemCopyState).filter({ status: ItemService.itemCopyStatuses.ItemTypeCannotBeCopied }).map('item').value();

                if (itemsToCopy.length === 0) {
                    DialogService.alert('None of the selected items can be copied.')
                        .then(function () { return $q.reject(); });
                } else {
                    return DialogService.show({
                        locals: { itemCopyState: itemCopyState, preCopyConfirmMode: true },
                        controller: 'ItemCopySummaryDialogCtrl',
                        controllerAs: 'vm',
                        templateUrl: 'app/Item/item-copy-summary-dialog.tmpl.html'
                    });
                }
            }

            /**
             * Copy all items in the "ToBeCopied" state.
             */
            function copyItems() {
                var itemIdsToCopy = _.chain(itemCopyState).filter({ status: ItemService.itemCopyStatuses.ToBeCopied }).map('item').map('Id').value();

                if (itemIdsToCopy.length > 0) {
                    ItemService.copyItems(itemIdsToCopy)
                        .then(copyItemsOk, copyItemsError)
                        .finally(updateList);
                } else {
                    showCopySummaryDialog();
                    updateList();
                }
            }

            /**
             * Copy items response handler.
             */
            function copyItemsOk(copyResponse) {

                //
                // Merge copyResponse details into itemCopyState
                //

                _.forEach(copyResponse.ItemsCopied, function (itemId) {
                    var itemState = _.find(itemCopyState, { item: { Id: itemId } });
                    if (!_.isNil(itemState)) {
                        itemState.status = ItemService.itemCopyStatuses.Copied;
                    }
                });

                _.forEach(copyResponse.ItemsOfNoCopyType, function (itemId) {
                    var itemState = _.find(itemCopyState, { item: { Id: itemId } });
                    if (!_.isNil(itemState)) {
                        itemState.status = ItemService.itemCopyStatuses.ItemTypeCannotBeCopied;
                    }
                });

                if (!_.isNil(copyResponse.ItemCopyError) && copyResponse.ItemCopyError) {

                    // Error copying, show user a dialog for them to decide whether to continue copying.

                    var itemState = _.find(itemCopyState, { item: { Id: copyResponse.ItemProducingError } });
                    var itemProducingError = null;
                    if (!_.isNil(itemState)) {
                        itemState.status = ItemService.itemCopyStatuses.Error;
                        itemProducingError = itemState.item;
                    }

                    DialogService.show({
                        locals: { errorText: copyResponse.ItemCopyError, itemProducingError: itemProducingError, itemCopyState: itemCopyState },
                        controller: 'ItemCopyErrorDialogCtrl',
                        controllerAs: 'vm',
                        templateUrl: 'app/Item/item-copy-error-dialog.tmpl.html'
                    }).then(copyItems, showCopySummaryDialog);
                } else {
                    // Copy finished, show summary
                    showCopySummaryDialog();
                }
            }

            /**
             * Display dialog showing summary of which items were copied.
             */
            function showCopySummaryDialog() {
                DialogService.show({
                    locals: { itemCopyState: itemCopyState, preCopyConfirmMode: false },
                    controller: 'ItemCopySummaryDialogCtrl',
                    controllerAs: 'vm',
                    templateUrl: 'app/Item/item-copy-summary-dialog.tmpl.html'
                });
            }

            /**
             * Copy items error handler.
             */
            function copyItemsError(response) {
                DialogService.error('Error copying items', response);
            }
        }

        /**
         * Bulk edit the selected items.
         */
        function onBulkEdit() {
            if (!ensureItemSelection()) {
                return;
            }

            // Convert list of selected items into "item edit state" list
            var itemEditState = [];
            _.forEach(_.filter(drvCtrl.items, 'Selected'), function (item) {
                var itemMetaData = ItemService.getItemTypeMetaData(item);

                itemEditState.push({
                    item: item,
                    status: itemMetaData && itemMetaData.canMove ? 'ToBeMoved' : 'ItemTypeCannotBeMoved'
                });
            });

            var entityIdToMoveTo;

            preProcessSelection();


            /**
             * Pre-process the item selection. Depending on the quantity and type of selection, will:
             *
             * - Show an error that no items can be edited.
             * - Go to an edit view for a single item.
             * - Show a dialog to choose the entity to move items to.
             */
            function preProcessSelection() {
                var itemsToEdit = _.chain(itemEditState).filter({ status: 'ToBeMoved' }).map('item').value();

                if (itemsToEdit === 0) {
                    DialogService.alert('None of the selected items can be bulk edited.')
                        .then(function () { return $q.reject(); });
                } else if (itemsToEdit === 1) {
                    $location.url(selectedItems[0].editLink);
                } else {
                    DialogService.show({
                        locals: { itemEditState: itemEditState },
                        controller: 'ItemBulkEditDialogCtrl',
                        controllerAs: 'vm',
                        templateUrl: 'app/Item/item-bulk-edit-dialog.tmpl.html'
                    }).then(function (entityId) {
                        entityIdToMoveTo = entityId;
                        runBulkEdit();
                    });
                }
            }

            /**
             * Send request to server to perform bulk edit operation.
             */
            function runBulkEdit() {
                var itemIdsToCopy = _.chain(itemEditState).filter({ status: 'ToBeMoved' }).map('item').map('Id').value();

                ItemService.bulkEdit(itemIdsToCopy, entityIdToMoveTo)
                    .then(bulkEditOk, bulkEditError)
                    .finally(updateList);
            }

            /**
             * Bulk edit items response handler.
             */
            function bulkEditOk(editResponse) {

                //
                // Merge editResponse details into itemEditState
                //

                _.forEach(editResponse.ItemsEdited, function (itemId) {
                    var itemState = _.find(itemEditState, { item: { Id: itemId } });
                    if (!_.isNil(itemState)) {
                        itemState.status = 'Moved';
                    }
                });

                _.forEach(editResponse.ItemsOfNoEditType, function (itemId) {
                    var itemState = _.find(itemEditState, { item: { Id: itemId } });
                    if (!_.isNil(itemState)) {
                        itemState.status = 'ItemTypeCannotBeMoved';
                    }
                });


                if (!_.isNil(editResponse.ItemEditError) && editResponse.ItemEditError) {

                    // Error editing items, show user a dialog for them to decide whether to continue.

                    var itemState = _.find(itemEditState, { item: { Id: editResponse.ItemProducingError } });
                    var itemProducingError = null;
                    if (!_.isNil(itemState)) {
                        itemState.status = 'Error';
                        itemProducingError = itemState.item;
                    }

                    DialogService.show({
                        locals: {
                            errorText: editResponse.ItemEditError,
                            itemProducingError: itemProducingError,
                            itemEditState: itemEditState
                        },
                        controller: 'ItemBulkEditErrorDialogCtrl',
                        controllerAs: 'vm',
                        templateUrl: 'app/Item/item-bulk-edit-error-dialog.tmpl.html'
                    }).then(runBulkEdit, showBulkEditSummaryDialog);

                } else {
                    // Bulk edit finished, show summary
                    showBulkEditSummaryDialog();
                }
            }

            /**
             * Display dialog showing summary of which items were edited.
             */
            function showBulkEditSummaryDialog() {
                DialogService.show({
                    locals: {
                        itemEditState: itemEditState
                    },
                    controller: 'ItemBulkEditSummaryDialogCtrl',
                    controllerAs: 'vm',
                    templateUrl: 'app/Item/item-bulk-edit-summary-dialog.tmpl.html'
                });
            }

            /**
             * Bulk edit items error handler.
             */
            function bulkEditError(response) {
                DialogService.error('Error bulk editing items', response);
            }
        }
    }
})();
